"use client";

import { Component, type ReactNode } from "react";
import Link from "next/link";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary that catches WebGL / R3F initialization failures.
 * If the <Canvas> crashes (e.g. WebGL unsupported, context lost),
 * this renders a graceful fallback instead of a blank screen.
 */
class ForestErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Forest scene error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex h-screen w-screen flex-col items-center justify-center bg-[#070710] text-white/60">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="mb-6 text-emerald-400/40"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          <h1 className="mb-2 text-sm font-medium tracking-wide text-white/80">
            Couldn&apos;t start the 3D forest
          </h1>
          <p className="mb-8 max-w-sm text-center text-xs text-white/40">
            The browser couldn&apos;t get a graphics (WebGL) context — usually from
            too many 3D/heavy tabs open at once, or a graphics hiccup. Closing
            other heavy tabs and reloading almost always fixes it.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-5 py-2 text-xs text-emerald-300/90 transition-all hover:bg-emerald-500/20"
            >
              Reload
            </button>
            <Link
              href="/"
              className="rounded-full border border-white/10 bg-white/5 px-5 py-2 text-xs text-white/50 transition-all hover:border-emerald-500/30 hover:text-white/80"
            >
              Back to site
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ForestErrorBoundary;
